<template>
  <CModal
    :show="show"
    :closeOnBackdrop="false"
    :centered="true"
    size="xl"
    id="editInvoice"
  >
    <template #header>
      <h5 class="modal-title">{{ title }}: {{ invoiceDestinatary() }}</h5>
    </template>

    <div v-if="invoice !== null">
      <p>
        {{ $t("role.finance.invoiceEditModal.instructions") }}
      </p>

      <CAlert v-if="error.hasGeneralErrors()" color="danger" class="mb-3">
        {{ error.general().join(" ") }}
      </CAlert>

      <form action="" @submit.prevent="onSubmit" ref="form">
        <CRow>
          <CCol md="12">
            <CInput
              v-model="search"
              :placeholder="
                $t('role.finance.invoiceEditModal.searchPlaceholder')
              "
            >
              <template #prepend-content>
                <font-awesome-icon icon="search" />
              </template>
            </CInput>
          </CCol>
        </CRow>

        <div class="d-flex justify-content-center m-3" v-if="loadingDetails">
          <CSpinner color="info" />
        </div>

        <div
          class="
            table-responsive
            details-table-wrapper
            bg-white
            rounded
            border-gray-200
          "
          v-else
        >
          <table class="table">
            <thead>
              <tr>
                <th></th>
                <th>{{ $t("role.finance.invoiceEditModal.table.name") }}</th>
                <th>{{ $t("role.finance.invoiceEditModal.table.qta") }}</th>
                <th>
                  {{ $t("user.marketCart.price") }}
                  {{ $t("family.market.product.vatexcl") }}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <fragment v-for="group in groups" :key="group.id">
                <tr class="highlighted" style="cursor: pointer">
                  <td width="40">
                    <CInputCheckbox
                      inline
                      custom
                      :checked="checkSelected(group)"
                      :disabled="
                        isGroupSelectordDisabled(group) ||
                        isLastSelectedGroup(group)
                      "
                      @update:checked="
                        (checked) => onCheckboxGroupChange(checked, group)
                      "
                    />
                  </td>
                  <td colspan="4" @click="toggleGroup(group)">
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <strong>{{ group.label }}</strong>
                      <font-awesome-icon
                        :icon="['fa', 'caret-up']"
                        v-if="group.collapsed"
                      />
                      <font-awesome-icon :icon="['fa', 'caret-down']" v-else />
                    </div>
                  </td>
                </tr>
                <template v-if="!group.collapsed">
                  <tr
                    v-for="detail in searchResults(group.details)"
                    :key="detail.id"
                  >
                    <td width="40">
                      <CInputCheckbox
                        inline
                        custom
                        :checked.sync="detail.select"
                        :disabled="
                          detail.disabled || isLastSelectedDetail(detail)
                        "
                        @update:checked="
                          (checked) => onCheckboxChange(checked, detail)
                        "
                      />
                    </td>
                    <td>
                      <strong>{{ productName(detail) }}</strong>
                      <div>{{ variantName(detail) }}</div>
                    </td>
                    <td>{{ quantity(detail) }}</td>
                    <td>
                      <strong>{{ price(detail) }} </strong>
                    </td>
                    <td></td>
                  </tr>
                </template>
              </fragment>
            </tbody>
          </table>
        </div>
      </form>
    </div>

    <template #footer>
      <!--
      <CRow
        class="justify-content-center align-items-end mb-2 pt-3 px-2 border-top"
      >
        <CCol class="p-0 pb-2">
          <div class="d-flex align-items-center justify-content-start">
            <CInputCheckbox
              inline
              custom
              :checked.sync="extraProductActive"
              class="pr-2 m-0"
            />
            <h5 class="m-0">Zusatzprodukt / Zusatzgebühr</h5>
          </div>
          <div v-if="extraProductActive" class="mt-2">
            <CInput
              v-model="extraProductData.description"
              :disabled="!extraProductActive"
              class="mb-2"
              :label="$t('models.role.logo_rect_info')"
            />
            <div class="d-flex align-items-center justify-content-start">
              <CInput
                :disabled="!extraProductActive"
                v-model="extraProductData.price"
                class="mb-0"
                :append="currencySymbol"
                type="number"
                min="0"
                step="1"
                :label="$t('user.marketCart.price')"
              />
              <CSelect
                :disabled="!extraProductActive"
                v-model="extraProductData.vat"
                class="m-0 ml-3"
                :options="taxRates"
                :label="$t('role.finance.invoiceEditModal.table.tax')"
              />
            </div>
          </div>
        </CCol>
      </CRow>
      -->

      <CRow
        class="
          justify-content-start
          align-items-center
          mb-2
          py-3
          mb-1
          border-top
          px-2
        "
      >
        <div class="p-0">
          <div class="d-flex align-items-center justify-content-start mb-2">
            <CInputCheckbox
              inline
              custom
              :checked.sync="discountActive"
              class="pr-2 m-0"
            />
            <h5 class="m-0">
              {{ $t("role.finance.invoiceEditModal.discount") }}
            </h5>
          </div>
          <CInput
            v-if="discountActive"
            v-model="discount"
            class="mb-0"
            :append="currencySymbol"
            type="number"
            min="0"
            step="1"
          />
        </div>
      </CRow>

      <CRow
        class="
          justify-content-end
          align-items-center
          border-top
          p-2
          px-3
          rounded
          border
          bg-white
        "
      >
        <CCol class="text-left p-0"
          ><div class="ui-type-subheading text-uppercase text-gray-600">
            {{ $t("role.finance.invoiceEditModal.total") }}
            {{ $t("family.market.product.vatexcl") }}
          </div>
          <h4 class="mb-0">
            {{ !loadingDetails ? invoiceAmount() : "--" }}
          </h4>
        </CCol>
        <CCol class="text-right p-0">
          <CButton
            color="primary"
            variant="outline"
            @click.prevent="close"
            class="mr-2"
            >{{ $t("role.finance.invoiceEditModal.cancelBtn") }}</CButton
          >
          <vue-ladda
            :loading="loading"
            :disabled="loadingDetails || loading"
            data-style="zoom-in"
            button-class="btn btn-primary px-4"
            @click.prevent="onSubmit"
            >{{ $t("role.finance.invoiceEditModal.confirmBtn") }}</vue-ladda
          >
        </CCol>
      </CRow>
    </template>
  </CModal>
</template>

<script>
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import EventBus from "../../../../helpers/EventBus";
import {
  formatDate,
  formatPrice,
  getOptionLabel,
} from "../../../../helpers/common";
import errorResponse from "../../../../helpers/error";
import { mapGetters } from "vuex";
import { ApiCall, GetDataManagerNew } from "../../../../ds";
import { Query } from "@syncfusion/ej2-data";
import { INVOICE_TYPE } from "../../../../config/global";
import { getPriceUnitOptions } from "../../../../helpers/options";
import { getTaxRateOptions } from "../../../../helpers/options";

export default {
  name: "InvoicesEditModal",

  data() {
    return {
      loadingDetails: false,
      loading: false,
      error: errorResponse(),
      search: "",
      discount: 0,
      groups: [],
      extraProductData: {
        description: "",
        price: 0,
        vat: 0,
      },
      extraProductActive: false,
      discountActive: false,
      taxRates: [],
    };
  },

  watch: {
    show(value) {
      // reset on open/close modal
      if (value) {
        this.reset();
        this.loadDetails();
      }
    },
  },

  computed: {
    title() {
      return this.$t("role.finance.invoiceEditModal.title");
    },

    show() {
      return this.$store.state.invoices.edit.show;
    },

    invoice() {
      return this.$store.state.invoices.edit.invoice;
    },

    lang() {
      return this.$i18n.locale;
    },

    ...mapGetters("connections", ["currencySymbol"]),
  },

  methods: {
    reset() {
      this.loadingDetails = false;
      this.loading = false;
      this.error.reset();
      this.search = "";
      this.discount = get(
        this,
        "$store.state.invoices.edit.invoice.discount",
        0
      );
      this.invoiceAmountOrig =
        get(this.invoice, "price") + get(this.invoice, "discount", 0);
      this.groups = [];

      this.extraProductActive = false;
      this.extraProductData = {
        description: "",
        price: 0,
        vat: 0,
      };
    },

    makeGroups(rows, invoice_type) {
      const groups = rows.reduce((groups, row) => {
        let groupKey = row.id;
        let grouplabel = "";

        switch (invoice_type) {
          case INVOICE_TYPE.CUSTOMER:
            groupKey = get(row, "orderdetail.order.id").toString();
            grouplabel = `${this.$t(
              "role.finance.invoiceConfirmModal.order"
            )} #${groupKey}`;
            break;
          case INVOICE_TYPE.PRODUCER:
            groupKey = get(row, "orderdetail.ddmp.delivery_date").toISOString();
            grouplabel = `${this.$t(
              "role.finance.invoiceConfirmModal.close-order"
            )} ${formatDate(new Date(groupKey), "dd-MM-yyyy · HH:mm")}`;
            break;
          case INVOICE_TYPE.ISLAND:
          case INVOICE_TYPE.MARKET:
            groupKey = get(
              row,
              "orderdetail.order.ddmi.delivery_date"
            ).toISOString();
            grouplabel = `${this.$t(
              "role.finance.invoiceConfirmModal.close-order"
            )} ${formatDate(new Date(groupKey), "dd-MM-yyyy · HH:mm")}`;
            break;
        }

        const group = groups.find((g) => g.id === groupKey);
        const detail = { ...row, disabled: false };

        if (!group) {
          groups.push({
            id: groupKey,
            label: grouplabel,
            details: [detail],
            collapsed: false,
          });
        } else {
          group.details.push(detail);
        }
        return groups;
      }, []);

      return groups;
    },

    loadDetails() {
      this.loadingDetails = true;
      const roleId = this.$store.state.role.id;
      const invoiceId = this.invoice.id;

      ApiCall(
        GetDataManagerNew("role_invoices_toemit_details", [roleId, invoiceId]),
        new Query(),
        (response) => {
          this.groups = this.makeGroups(response.result, this.invoice.i_type);
          this.loadingDetails = false;
        },
        (error) => {
          console.error(error);
          this.loadingDetails = false;
        }
      );
    },

    close() {
      this.$store.commit("invoices/closeEditModal");
    },

    invoiceDestinatary() {
      return this.invoice.i_to.name;
    },

    invoiceAmount() {
      let amount = 0;
      if (this.groups.length > 0) {
        const totalAmount = this.groups.reduce((acc, group) => {
          const groupTotalAmount = group.details
            .filter((d) => d.select)
            .reduce((a, d) => a + d.price, 0);
          return acc + groupTotalAmount;
        }, 0);

        amount = totalAmount - this.discount;
      }
      return formatPrice(amount, this.currencySymbol).format();
    },

    productName(detail) {
      return get(
        detail,
        `orderdetail.ppm.lot.productproducer.translations.${this.lang}.name`
      );
    },

    variantName(detail) {
      return get(detail, `orderdetail.ppm.lot.translations.${this.lang}.name`);
    },

    quantity(detail) {
      return `${get(detail, `orderdetail.qta`)}${
        this.isWeightProduct(detail) ? ` ${this.pwUnit(detail)}` : ""
      }`;
    },

    pwUnit(detail) {
      return getOptionLabel(
        get(detail, `orderdetail.ppm.lot.productproducer.pw_unit`),
        getPriceUnitOptions(this.$i18n)
      );
    },

    isWeightProduct(detail) {
      return get(detail, "orderdetail.ppm.lot.productproducer.pw", false);
    },

    taxRate(detail) {
      return get(detail, `orderdetail.ppm.lot.tax_rate.rate`);
    },

    price(detail) {
      return formatPrice(detail.price, this.currencySymbol).format();
    },

    checkSelected(group) {
      return (
        group.details.length ===
        group.details.filter((item) => item.select === true).length
      );
    },

    isGroupSelectordDisabled(group) {
      return group.details.filter((item) => item.disabled === true).length > 0;
    },

    onCheckboxGroupChange(checked, group) {
      const groupsClone = cloneDeep(this.groups);
      const g = this.groups.find((g) => g.id === group.id);

      const ids = [];
      if (g) {
        g.details.forEach((product) => {
          product.disabled = true;
          ids.push(product.id);
        });

        const roleId = this.$store.state.role.id;
        const invoiceId = this.invoice.id;
        this.error.reset();

        this.$store
          .dispatch("invoices/setInvoiceDetailSelected", {
            roleId,
            invoiceId,
            ids: ids,
            select: checked,
          })
          .then(() => {
            g.details.forEach((product) => {
              product.select = checked;
              product.disabled = false;
            });
          })
          .catch((error) => {
            this.error.set(error.response.data.errors);
            this.groups = groupsClone;
          });
      }
    },

    onCheckboxChange(checked, detail) {
      detail.disabled = true;
      const roleId = this.$store.state.role.id;
      const invoiceId = this.invoice.id;
      this.error.reset();
      this.$store
        .dispatch("invoices/setInvoiceDetailSelected", {
          roleId,
          invoiceId,
          ids: [detail.id],
          select: checked,
        })
        .then(() => {
          detail.selected = checked;
        })
        .catch((error) => {
          detail.selected = !checked;
          this.error.set(error.response.data.errors);
        })
        .finally(() => {
          detail.disabled = false;
        });
    },

    countSelectedDetails() {
      if (!this.groups || this.groups.length === 0) return 0;
      return this.groups.reduce((acc, g) => {
        return acc + g.details.filter((d) => d.select === true).length;
      }, 0);
    },

    countGroupsWithSelected() {
      if (!this.groups || this.groups.length === 0) return 0;
      return this.groups.filter((g) => g.details.some((d) => d.select === true))
        .length;
    },

    isLastSelectedDetail(detail) {
      return detail.select === true && this.countSelectedDetails() === 1;
    },

    isLastSelectedGroup(group) {
      const hasSelected = group.details.some((d) => d.select === true);
      return hasSelected && this.countGroupsWithSelected() === 1;
    },

    toggleGroup(group) {
      group.collapsed = !group.collapsed;
    },

    searchResults(rows) {
      const q = (this.search || "").toString().trim().toLowerCase();
      if (!q) return rows;

      return rows.filter((item) => {
        const name = (this.productName(item) || "").toString().toLowerCase();
        const variant = (this.variantName(item) || "").toString().toLowerCase();
        return name.includes(q) || variant.includes(q);
      });
    },

    onSubmit() {
      const roleId = this.$store.state.role.id;
      const invoiceId = this.invoice.id;
      const discount = this.discount;

      this.error.reset();
      this.loading = true;
      this.$store
        .dispatch("invoices/editInvoice", { roleId, invoiceId, discount })
        .then(() => {
          this.close();
          EventBus.$emit("invoices:refresh");
        })
        .catch((error) => {
          this.error.set(error.response.data.errors);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    toggleExtraProduct(extraProductActive) {
      this.extraProductActive = !extraProductActive;
    },

    extraProductData(checked) {
      return;
    },
  },

  async created() {
    this.taxRates = await getTaxRateOptions({
      country_id: this.$store.state.role.country_id,
    });
  },
};
</script>

<style scoped>
#editInvoice >>> .modal-footer {
  display: block;
}
.details-table-wrapper {
  max-height: 350px;
  overflow-y: scroll;
}
</style>
